import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sanitizeMarketingHtml, sanitizeStyleBlock } from '../utils/sanitizeHtml';

/**
 * Marketing landing page.
 * Source of truth for the marketing site is /public/site/index.html.
 * This component fetches that file, injects its <style> + <body> contents
 * into the React tree, then re-attaches the small inline behaviour
 * (feature-tab switcher, reveal-on-scroll observer, compare modal toggle)
 * and intercepts internal <a> clicks to use react-router navigation.
 */
export default function Landing() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [styleHtml, setStyleHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  // Auth redirect — same behaviour as before.
  useEffect(() => {
    if (!loading && isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, loading, navigate]);

  // Inject Google Fonts <link> once at document head.
  useEffect(() => {
    const id = 'ludy-marketing-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,400;0,500;0,700;1,400;1,700&family=Space+Grotesk:wght@400;500;600;700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap';
    document.head.appendChild(link);
  }, []);

  // Fetch the marketing HTML once.
  useEffect(() => {
    let cancelled = false;
    fetch('/site/index.html')
      .then(res => res.text())
      .then(html => {
        if (cancelled) return;
        const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
        const body = (bodyMatch?.[1] || '')
          // remove inline <script> tags — re-attached below
          .replace(/<script[\s\S]*?<\/script>/g, '');
        setStyleHtml(styleMatch?.[1] || '');
        setBodyHtml(body);
      });
    return () => { cancelled = true; };
  }, []);

  // After body is injected: re-wire JS behaviour + intercept SPA links.
  useEffect(() => {
    if (!bodyHtml || !containerRef.current) return;
    const root = containerRef.current;

    // 1. Feature tab switcher (originally onclick="switchFeature(this, idx)")
    const previews = [0, 1, 2, 3].map(i => root.querySelector('#preview-' + i));
    const featureItems = root.querySelectorAll('.feature-item');
    const switchFeature = (el, idx) => {
      featureItems.forEach(f => f.classList.remove('active'));
      el.classList.add('active');
      previews.forEach((p, i) => { if (p) p.style.display = i === idx ? 'flex' : 'none'; });
    };
    featureItems.forEach((item, idx) => {
      // strip baked-in onclick attributes which won't fire (CSP-ish behaviour)
      item.removeAttribute('onclick');
      item.addEventListener('click', () => switchFeature(item, idx));
    });

    // 2. Compare modal show/hide
    const modal = root.querySelector('#compare-modal');
    const openBtn = root.querySelector('button[onclick*="compare-modal"][onclick*="flex"]');
    const closeBtns = root.querySelectorAll('button[onclick*="compare-modal"][onclick*="none"], #compare-modal button');
    if (modal) {
      modal.removeAttribute('onclick');
      modal.addEventListener('click', (e) => { if (e.target === modal) modal.style.display = 'none'; });
    }
    if (openBtn) {
      openBtn.removeAttribute('onclick');
      openBtn.removeAttribute('onmouseover');
      openBtn.removeAttribute('onmouseout');
      openBtn.addEventListener('click', () => { if (modal) modal.style.display = 'flex'; });
    }
    closeBtns.forEach(btn => {
      btn.removeAttribute('onclick');
      btn.removeAttribute('onmouseover');
      btn.removeAttribute('onmouseout');
      if (btn.textContent.trim() === '×') {
        btn.addEventListener('click', () => { if (modal) modal.style.display = 'none'; });
      }
    });

    // 3. Reveal-on-scroll IntersectionObserver
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.1 });
    root.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    // 4. Role tabs (Sales Rep / Team Leader) — toggle active panel
    const roleTabs = root.querySelectorAll('.role-tab');
    const rolePanels = root.querySelectorAll('.role-panel');
    roleTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const role = tab.getAttribute('data-role');
        roleTabs.forEach(t => {
          const isActive = t === tab;
          t.classList.toggle('active', isActive);
          t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        rolePanels.forEach(p => {
          p.classList.toggle('active', p.getAttribute('data-panel') === role);
        });
      });
    });

    // 4b. Sticky nav — switch to white "scrolled" state once user moves past the hero.
    const navEl = root.querySelector('nav');
    const onScroll = () => {
      if (!navEl) return;
      navEl.classList.toggle('scrolled', window.scrollY > 40);
    };
    onScroll(); // initialise based on current scroll
    window.addEventListener('scroll', onScroll, { passive: true });

    // 4c. Mobile hamburger toggle
    const burger = root.querySelector('.nav-burger');
    const onBurgerClick = (e) => {
      e.stopPropagation();
      if (!navEl) return;
      const open = navEl.classList.toggle('menu-open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    const closeMenu = () => {
      if (!navEl) return;
      navEl.classList.remove('menu-open');
      if (burger) burger.setAttribute('aria-expanded', 'false');
    };
    if (burger) burger.addEventListener('click', onBurgerClick);

    // 5. Intercept internal links (/auth, /pricing, etc.) to use SPA navigation.
    const onLinkClick = (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href) return;
      // Close mobile menu after any link click
      closeMenu();
      // Only intercept same-origin absolute paths, ignore mailto:, hash anchors, and external URLs
      if (href.startsWith('mailto:') || href.startsWith('http')) return;
      if (href.startsWith('#')) return; // let browser handle hash anchors natively
      if (href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
      }
    };
    root.addEventListener('click', onLinkClick);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      root.removeEventListener('click', onLinkClick);
      if (burger) burger.removeEventListener('click', onBurgerClick);
    };
  }, [bodyHtml, navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FBFAF6' }}>
        <div className="cyber-spinner"></div>
      </div>
    );
  }

  return (
    <>
      {styleHtml && <style dangerouslySetInnerHTML={{ __html: sanitizeStyleBlock(styleHtml) }} />}
      <div
        ref={containerRef}
        data-testid="marketing-landing"
        dangerouslySetInnerHTML={{ __html: sanitizeMarketingHtml(bodyHtml) }}
      />
    </>
  );
}
